import { AfterViewInit, Component, ElementRef, OnInit, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { NewHireSettings } from '../../../../models/new-hire';
import { PlanSettings } from '../../../../models/plan';
import { DateUtils } from '../../../../common/utils/date';
import { DatePipe } from '@angular/common';
import {Store} from '@ngrx/store';
import { MultiSelect } from 'primeng/multiselect';
import { DatePicker } from 'primeng/datepicker';
import { associateMultiselectVisibleLabel, sanitizeMultiselectComboboxValue } from '../../../../common/utils/multiselect-a11y';

@Component({
  standalone: false,
  selector: 'esp-new-hire-class-dialog',
  templateUrl: './new-hire-class-dialog.component.html',
  styleUrls: ['./new-hire-class-dialog.component.css'],
  providers: [DatePipe]
})
export class NewHireClassDialogComponent implements OnInit, OnDestroy, AfterViewInit {

  public isLoading = false;
  public enableCancel = true;
  public enableAccept = false;
  public formSub: Subscription;
  public startingDateSub: Subscription;
  public endingDateSub: Subscription;

  private datePickerAriaObserver: MutationObserver | null = null;
  private comboboxA11yObserver: MutationObserver | null = null;
  private comboboxLabelObserver: MutationObserver | null = null;
  private muPanelEscapeHandler: ((e: KeyboardEvent) => void) | null = null;

  // Dragon voice-control: open-on-focus support (WCAG 2.1.1 / 4.1.2).
  private voiceFocusHandler: ((event: FocusEvent) => void) | null = null;
  // Open the calendar when the user clicks the date input (see
  // setupDateFieldClickActivation).
  private dateFieldClickHandler: ((event: MouseEvent) => void) | null = null;

  @ViewChild('muSelector') muSelector?: MultiSelect;
  @ViewChildren(DatePicker) datePickers?: QueryList<DatePicker>;

  public addClassForm: FormGroup;
  public managementUnits: any[] = [];
  private _selectedLearningCurve: any = null;
  public selectedMUs: any[] = [];
  private userInfo$: any;
  private locale: any = 'en-US';

  public get selectedLearningCurve(): any {
    return this._selectedLearningCurve;
  }

  public set selectedLearningCurve(value: any) {
    this._selectedLearningCurve = value;
    // Recalculate date constraints when learning curve changes
    if (this.maxDate) {
      this.calculateStartingDateMaxDate();
    }
    if (this.minDate) {
      this.calculateEndingDateMinDate();
    }
    // Recalculate disabled days when learning curve changes (for trainingWeeks = 0 case)
    this.calculateDisabledDays();
    // Recalculate default dates when learning curve changes
    this.calculateDefaultDates();
  }

  public virtualScrollOptions = { itemHeight: 40 };
  public _datesFormat: any;
  public _datePlaceholder: string;
  public isEditMode: boolean = false;
  public existingClassData: any = null;
  public newHireSettings: NewHireSettings;
  public planSettings: PlanSettings;

  // Min and max dates for the calendars
  public minDate: Date | null = null;
  public maxDate: Date | null = null;
  public startingDateMaxDate: Date | null = null;
  public endingDateMinDate: Date | null = null;
  public startingDateDisabledDays: number[] = [];
  public endingDateDisabledDays: number[] = [];
  public startingDateDefaultDate: Date | null = null;
  public endingDateDefaultDate: Date | null = null;
  date: any;

  constructor(
    public activeModal: NgbActiveModal,
    public translate: TranslateService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private store: Store<any>,
    private el: ElementRef,
  ) {
    this.addClassForm = this.fb.group({
      managementUnits: [[], [Validators.required]],
      fteCount: ['10', [Validators.required, Validators.min(0), Validators.max(99999.99)]],
      startingDate: ['', [Validators.required]],
      endingDate: ['', [Validators.required]]
    });

    // Subscribe to form value changes
    this.formSub = this.addClassForm.valueChanges.subscribe(() => {
      this.validateForm();
    });
    this.userInfo$ = this.store.select("state");
    this.userInfo$.subscribe((value: any) => {
      if (value && value.locale) {
        this.locale = value.locale;
      }
    });
  }

  ngOnInit(): void {
    moment.locale((this.translate.currentLang));
    this._datesFormat = this.translate.instant("date.picker.format");
    this._datePlaceholder = this.getDatePlaceholder(this._datesFormat);

    // Set min and max dates based on new hire settings
    this.minDate = this.newHireSettings?.newHireStartDate ? this.createLocalDateFromISOString(this.newHireSettings.newHireStartDate) : null;
    this.maxDate = this.newHireSettings?.newHireEndDate ? this.createLocalDateFromISOString(this.newHireSettings.newHireEndDate) : null;

    // Calculate disabled days based on minDate and maxDate
    this.calculateDisabledDays();

    // Calculate starting date maximum date based on training weeks
    this.calculateStartingDateMaxDate();

    // Calculate ending date minimum date based on training weeks
    this.calculateEndingDateMinDate();

    // Calculate default dates based on current date vs plan date range
    this.calculateDefaultDates();

    // Set up date change subscriptions for automatic calculation
    this.setupDateChangeSubscriptions();

    // Initialize form for edit mode if existing data is provided
    if (this.isEditMode && this.existingClassData) {
      this.initializeEditMode();
    }

    this.validateForm();
  }

  ngAfterViewInit(): void {
    this.setupDatePickerAriaControls();
    // WCAG 2.5.3 Label in Name / 4.1.2 (Dragon voice control): give the
    // Management Unit combobox a descriptive accessible name that contains its
    // visible label and selection text, and strip PrimeNG's "[object Object]"
    // value so "Click Management Unit" reliably targets the control.
    this.comboboxA11yObserver = sanitizeMultiselectComboboxValue(this.el?.nativeElement);
    this.comboboxLabelObserver = associateMultiselectVisibleLabel(this.el?.nativeElement);
    // WCAG 2.1.1 Keyboard / 4.1.2 (Dragon voice control): make the Management
    // Unit combobox and the date fields open themselves when they receive
    // focus, so each is a single operable control by voice.
    this.setupVoiceControlActivation();
    // Open the date-range calendar when the user clicks the Starting/Ending
    // Date input itself (not just the small calendar icon).
    this.setupDateFieldClickActivation();

    // a11y (WCAG 2.1.1 Keyboard): Register the MU dropdown Escape handler HERE
    // (before any panel opens) so our document capture listener is always
    // registered before PrimeNG registers its own per-panel Escape handler.
    // PrimeNG's handler shifts focus to its hidden combobox input without
    // closing the panel; registering after (onShow) would put us behind PrimeNG
    // in the queue and stopImmediatePropagation would be too late.
    this.muPanelEscapeHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !this.muSelector?.overlayVisible) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.muSelector.hide();
      // Return focus to the combobox trigger.
      const trigger = (this.el.nativeElement as HTMLElement)
        .querySelector<HTMLElement>('[role="combobox"]');
      trigger?.focus();
    };
    document.addEventListener('keydown', this.muPanelEscapeHandler, true);
  }

  /**
   * WCAG 2.1.1 Keyboard / 4.1.2 Name, Role, Value (Dragon voice control).
   *
   * Dragon activates a control by moving keyboard focus to it, not by firing a
   * bubbling DOM click that PrimeNG's open-on-click logic depends on. PrimeNG's
   * p-multiselect does NOT open its panel on focus (only on a container click,
   * and `onContainerClick` explicitly ignores clicks whose target is the
   * visually hidden combobox <input>), so "Click Management Unit" left the field
   * focused but the panel closed. p-datepicker DOES open on focus via
   * `showOnFocus`, but we also open it here so the field reliably opens however
   * Dragon moves focus onto it.
   *
   * We listen for `focusin` on the dialog host (it bubbles and fires for
   * programmatic focus). When focus lands on the combobox input or a date input
   * we open the matching overlay. A short guard after the multiselect panel
   * hides prevents an immediate reopen when PrimeNG returns focus to the
   * combobox input on close.
   */
  private setupVoiceControlActivation(): void {
    const host: HTMLElement | undefined = this.el?.nativeElement;
    if (!host || typeof host.addEventListener !== 'function') {
      return;
    }
    this.voiceFocusHandler = (event: FocusEvent) =>
      this.openControlForFocus(event.target as HTMLElement | null);
    host.addEventListener('focusin', this.voiceFocusHandler);
  }

  /**
   * Open the Management Unit combobox or a date-picker overlay when its control
   * receives focus, so voice-control users can operate the field with a single
   * command. Public for unit testing.
   */
  openControlForFocus(target: HTMLElement | null): void {
    // Intentionally does NOT auto-open any overlay on focus. Both the Management
    // Unit combobox and the date pickers open only on explicit user action
    // (click / Enter / Space), so focusing a field never opens its overlay.
  }

  /**
   * The Starting/Ending Date fields use `readonlyInput` + `showOnFocus=false`,
   * so PrimeNG's own `onInputClick` is disabled and clicking the date INPUT
   * does nothing — the user has to hit the small calendar icon. Reporters
   * expect clicking the date field to open the calendar (as it does by
   * default). We restore that by opening the owning date picker when the input
   * itself is clicked, while keeping `showOnFocus=false` so passive keyboard
   * Tab focus still does NOT auto-open the calendar (and there is no
   * reopen-after-select loop, which is a focus-only behaviour).
   */
  private setupDateFieldClickActivation(): void {
    const host: HTMLElement | undefined = this.el?.nativeElement;
    if (!host || typeof host.addEventListener !== 'function') {
      return;
    }
    this.dateFieldClickHandler = (event: MouseEvent) =>
      this.openDatePickerForClick(event.target as HTMLElement | null);
    host.addEventListener('click', this.dateFieldClickHandler);
  }

  /**
   * Open the date-picker overlay that owns the clicked Starting/Ending Date
   * input. Scoped to the date INPUT only (not the calendar icon button, whose
   * own toggle PrimeNG already handles) so clicking the icon still toggles and
   * clicking a day inside the open panel is untouched. Public for unit testing.
   */
  openDatePickerForClick(target: HTMLElement | null): void {
    const input = target?.closest?.('input#startingDate, input#endingDate') as HTMLElement | null;
    if (!input) {
      return;
    }
    const picker = this.datePickers?.find(dp => dp.el.nativeElement.contains(input));
    if (picker && !picker.overlayVisible) {
      picker.showOverlay();
    }
  }

  /**
   * Record when the Management Unit panel hides so open-on-focus does not
   * immediately reopen it when PrimeNG returns focus to the combobox input.
   */
  onMuPanelHide(): void {
    // intentionally empty – kept for template binding
  }

  /**
   * WCAG 2.1.1 Keyboard / 4.1.2 Name, Role, Value (Dragon voice control):
   * PrimeNG renders the Management Unit combobox on a visually hidden
   * <input role="combobox">. Voice-control software (Dragon) targets that
   * accessible control and issues a click on it, but PrimeNG's own
   * onContainerClick explicitly ignores clicks whose target is that hidden
   * input, so "Click Management Unit" never opens the panel. We detect that
   * exact case and open the overlay ourselves so the control is operable by
   * voice. Real pointer clicks land on the visible container (handled by
   * PrimeNG) and never reach this branch, so behaviour is unchanged for them.
   */
  onManagementUnitContainerClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.getAttribute('role') === 'combobox' && this.muSelector && !this.muSelector.overlayVisible) {
      this.muSelector.show(true);
    }
  }

  /**
   * PrimeNG p-datepicker renders its trigger input as an ARIA combobox
   * (role="combobox", aria-haspopup="dialog") but only binds
   * [attr.aria-controls] to the overlay panel while the calendar is open,
   * clearing it on every change detection once closed. Assistive technologies
   * need the relationship to be present at all times, so we point each date
   * input at a stable, visually hidden placeholder (#startingDatePanel /
   * #endingDatePanel) whenever PrimeNG has left it empty (WCAG 4.1.2). When the
   * calendar opens, PrimeNG overwrites aria-controls with the live panel id and
   * our guard leaves that untouched; when it closes we restore the placeholder.
   */
  private setupDatePickerAriaControls(): void {
    const host: HTMLElement = this.el.nativeElement;
    const mappings: Array<[string, string]> = [
      ['#startingDate', 'startingDatePanel'],
      ['#endingDate', 'endingDatePanel'],
    ];

    const apply = () => {
      mappings.forEach(([inputSelector, panelId]) => {
        const input = host.querySelector(inputSelector) as HTMLElement | null;
        if (input && !input.getAttribute('aria-controls')) {
          input.setAttribute('aria-controls', panelId);
        }
      });
    };

    apply();
    this.datePickerAriaObserver = new MutationObserver(apply);
    this.datePickerAriaObserver.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-controls'],
    });
  }

  ngOnDestroy(): void {
    if (this.formSub) {
      this.formSub.unsubscribe();
    }
    if (this.startingDateSub) {
      this.startingDateSub.unsubscribe();
    }
    if (this.endingDateSub) {
      this.endingDateSub.unsubscribe();
    }
    this.datePickerAriaObserver?.disconnect();
    this.datePickerAriaObserver = null;
    this.comboboxA11yObserver?.disconnect();
    this.comboboxA11yObserver = null;
    this.comboboxLabelObserver?.disconnect();
    this.comboboxLabelObserver = null;
    if (this.voiceFocusHandler) {
      this.el?.nativeElement?.removeEventListener('focusin', this.voiceFocusHandler);
      this.voiceFocusHandler = null;
    }
    if (this.dateFieldClickHandler) {
      this.el?.nativeElement?.removeEventListener('click', this.dateFieldClickHandler);
      this.dateFieldClickHandler = null;
    }
    if (this.muPanelEscapeHandler) {
      document.removeEventListener('keydown', this.muPanelEscapeHandler, true);
      this.muPanelEscapeHandler = null;
    }
  }

  validateForm(): void {
    const formValid = this.addClassForm.valid;
    this.enableAccept = formValid;
  }

  submit(): void {
    if (this.enableAccept) {
      const formValue = this.addClassForm.value;

      let selectedOrdered;
      if (this.isEditMode) {
        // In edit mode, create a management unit object from the existing data
        selectedOrdered = [{
          oid: this.existingClassData.muOid,
          label: this.existingClassData.managementUnit
        }];
      } else {
        // In add mode, use the selected management units from the form
        selectedOrdered = this.managementUnits
          .filter(mu => mu.isSelected)
          .map(mu => mu.value);
      }

      // Create the class object - now handling multiple management units
      const startDate = formValue.startingDate instanceof Date ? formValue.startingDate : new Date(formValue.startingDate);
      const endDate = formValue.endingDate instanceof Date ? formValue.endingDate : new Date(formValue.endingDate);

      // Validate dates before converting to ISO string
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('Invalid date values in form submission');
        return;
      }

      const newClass = {
        managementUnits: selectedOrdered,
        fteCount: formValue.fteCount,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        learningCurveId: this.selectedLearningCurve.learningCurveId
      };

      this.isLoading = true;
      this.enableCancel = false;

      // Close the dialog and return the data
      this.activeModal.close(newClass);
    }
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  get isFormValid(): boolean {
    return this.addClassForm.valid;
  }

  get fteCountErrorMessage(): string {
    return this.translate.instant('new.hire.fte.count.validation.error');
  }

  updateSelectedMUs() {
    this.managementUnits.forEach((mu) => {
      mu.isSelected = this.selectedMUs.indexOf(mu.value) != -1;
    })
  }

  private getDatePlaceholder(dateFormat: string): string {
    // Convert date format to uppercase placeholder
    // mm/dd/yy -> MM/DD/YYYY
    // dd/mm/yy -> DD/MM/YYYY
    return dateFormat
      .replace(/mm/g, 'MM')
      .replace(/dd/g, 'DD')
      .replace(/yy/g, 'YYYY');
  }

  private initializeEditMode(): void {
    if (!this.existingClassData) return;

    // Remove the management units control from the form in edit mode
    this.addClassForm.removeControl('managementUnits');

    // Set the form values
    this.addClassForm.patchValue({
      fteCount: this.existingClassData.fteCount,
      startingDate: this.createLocalDateFromISOString(this.existingClassData.startingDate),
      endingDate: this.createLocalDateFromISOString(this.existingClassData.endingDate)
    });
  }

  getManagementUnitDisplayName(): string {
    if (!this.existingClassData) return '';

    // In edit mode, we can use the management unit name directly from the existing data
    return this.existingClassData.managementUnit || '';
  }

  private calculateDefaultDates(): void {
    const currentDate = new Date();
    const planStartDate = this.planSettings?.startDate ? this.createLocalDateFromISOString(this.planSettings.startDate) : null;
    const planEndDate = this.planSettings?.endDate ? this.createLocalDateFromISOString(this.planSettings.endDate) : null;

    // Check if current date is within plan date range
    const isCurrentDateInPlanRange = planStartDate && planEndDate &&
      currentDate >= planStartDate && currentDate <= planEndDate;

    if (isCurrentDateInPlanRange) {
      // Check if current date plus training weeks would exceed plan length
      if (this.selectedLearningCurve?.trainingWeeks && planEndDate) {
        const trainingEndDate = new Date(currentDate);
        trainingEndDate.setDate(trainingEndDate.getDate() + (this.selectedLearningCurve.trainingWeeks * 7) - 1);

        // If training would extend beyond plan end date, fallback to min date
        if (trainingEndDate > planEndDate) {
          this.startingDateDefaultDate = this.minDate;
          this.endingDateDefaultDate = this.minDate;
          return;
        }
      }

      // Set default to current date - the date picker will show the current month
      this.startingDateDefaultDate = currentDate;
      this.endingDateDefaultDate = currentDate;
    } else {
      // Set default to min date
      this.startingDateDefaultDate = this.minDate;
      this.endingDateDefaultDate = this.minDate;
    }
  }

  private calculateDisabledDays(): void {
    // Calculate disabled days for starting date based on minDate
    if (!this.minDate) {
      this.startingDateDisabledDays = [];
    } else {
      const allowedStartingDayOfWeek = this.minDate.getDay();
      this.startingDateDisabledDays = [];
      for (let i = 0; i <= 6; i++) {
        if (i !== allowedStartingDayOfWeek) {
          this.startingDateDisabledDays.push(i);
        }
      }
    }

    // Calculate disabled days for ending date
    // If trainingWeeks is 0, ending date should have same disabled days as starting date
    if (this.selectedLearningCurve?.trainingWeeks === 0) {
      this.endingDateDisabledDays = [...this.startingDateDisabledDays];
    } else {
      // Calculate disabled days for ending date based on maxDate
      if (!this.maxDate) {
        this.endingDateDisabledDays = [];
      } else {
        const allowedEndingDayOfWeek = this.maxDate.getDay();
        this.endingDateDisabledDays = [];
        for (let i = 0; i <= 6; i++) {
          if (i !== allowedEndingDayOfWeek) {
            this.endingDateDisabledDays.push(i);
          }
        }
      }
    }
  }


  private calculateStartingDateMaxDate(): void {
    if (!this.maxDate || !this.selectedLearningCurve?.trainingWeeks) {
      this.startingDateMaxDate = this.maxDate;
      return;
    }

    // Calculate the maximum starting date by subtracting training weeks from maxDate
    const maxStartingDate = new Date(this.maxDate);
    maxStartingDate.setDate(maxStartingDate.getDate() - (this.selectedLearningCurve.trainingWeeks * 7) + 1);

    // Ensure the maximum starting date falls on the correct day of week (minDate day)
    if (this.minDate) {
      const targetDayOfWeek = this.minDate.getDay();
      const currentDayOfWeek = maxStartingDate.getDay();
      const daysDifference = targetDayOfWeek - currentDayOfWeek;

      // Adjust to the previous occurrence of the target day if needed
      if (daysDifference > 0) {
        maxStartingDate.setDate(maxStartingDate.getDate() + daysDifference - 7);
      } else if (daysDifference < 0) {
        maxStartingDate.setDate(maxStartingDate.getDate() + daysDifference);
      }
    }

    this.startingDateMaxDate = maxStartingDate;
  }

  private calculateEndingDateMinDate(): void {
    if (!this.minDate || !this.selectedLearningCurve?.trainingWeeks) {
      this.endingDateMinDate = this.minDate;
      return;
    }

    // Calculate the minimum ending date by adding training weeks to minDate
    const minEndingDate = new Date(this.minDate);
    minEndingDate.setDate(minEndingDate.getDate() + (this.selectedLearningCurve.trainingWeeks * 7) - 1);

    // Ensure the minimum ending date falls on the correct day of week (maxDate day)
    if (this.maxDate) {
      const targetDayOfWeek = this.maxDate.getDay();
      const currentDayOfWeek = minEndingDate.getDay();
      const daysDifference = targetDayOfWeek - currentDayOfWeek;

      // Adjust to the next occurrence of the target day if needed
      if (daysDifference < 0) {
        minEndingDate.setDate(minEndingDate.getDate() + daysDifference + 7);
      } else if (daysDifference > 0) {
        minEndingDate.setDate(minEndingDate.getDate() + daysDifference);
      }
    }

    this.endingDateMinDate = minEndingDate;
  }

  private setupDateChangeSubscriptions(): void {
    // Subscribe to starting date changes
    this.startingDateSub = this.addClassForm.get('startingDate')?.valueChanges.subscribe(value => {
      if (value && this.selectedLearningCurve) {
        this.calculateEndingDateFromStarting(value);
      }
    });

    // Subscribe to ending date changes
    this.endingDateSub = this.addClassForm.get('endingDate')?.valueChanges.subscribe(value => {
      if (value && this.selectedLearningCurve) {
        this.calculateStartingDateFromEnding(value);
      }
    });
  }

  private calculateEndingDateFromStarting(startingDate: Date): void {
    if (!startingDate || !this.selectedLearningCurve) {
      return;
    }

    const start = new Date(startingDate);
    if (isNaN(start.getTime())) {
      return;
    }

    let endingDate: Date;

    // If trainingWeeks is 0, end date should be the same as start date
    if (this.selectedLearningCurve.trainingWeeks === 0) {
      endingDate = new Date(start);
    } else {
      // Calculate ending date by adding trainingWeeks
      endingDate = new Date(start);
      endingDate.setDate(endingDate.getDate() + (this.selectedLearningCurve.trainingWeeks * 7) - 1);

      // Ensure ending date falls on the correct day of week (maxDate day)
      if (this.maxDate) {
        const targetDayOfWeek = this.maxDate.getDay();
        const currentDayOfWeek = endingDate.getDay();
        const daysDifference = targetDayOfWeek - currentDayOfWeek;
        endingDate.setDate(endingDate.getDate() + daysDifference);
      }
    }

    // Update the ending date without triggering its value change subscription
    this.endingDateSub?.unsubscribe();
    this.addClassForm.get('endingDate')?.setValue(endingDate, { emitEvent: false });
    this.setupEndingDateSubscription();
  }

  private calculateStartingDateFromEnding(endingDate: Date): void {
    if (!endingDate || !this.selectedLearningCurve) {
      return;
    }

    const end = new Date(endingDate);
    if (isNaN(end.getTime())) {
      return;
    }

    let startingDate: Date;

    // If trainingWeeks is 0, start date should be the same as end date
    if (this.selectedLearningCurve.trainingWeeks === 0) {
      startingDate = new Date(end);
    } else {
      // Calculate starting date by subtracting trainingWeeks
      startingDate = new Date(end);
      startingDate.setDate(startingDate.getDate() - (this.selectedLearningCurve.trainingWeeks * 7) + 1);

      // Ensure starting date falls on the correct day of week (minDate day)
      if (this.minDate) {
        const targetDayOfWeek = this.minDate.getDay();
        const currentDayOfWeek = startingDate.getDay();
        const daysDifference = targetDayOfWeek - currentDayOfWeek;
        startingDate.setDate(startingDate.getDate() + daysDifference);
      }
    }

    // Update the starting date without triggering its value change subscription
    this.startingDateSub?.unsubscribe();
    this.addClassForm.get('startingDate')?.setValue(startingDate, { emitEvent: false });
    this.setupStartingDateSubscription();
  }

  private setupStartingDateSubscription(): void {
    this.startingDateSub = this.addClassForm.get('startingDate')?.valueChanges.subscribe(value => {
      if (value && this.selectedLearningCurve) {
        this.calculateEndingDateFromStarting(value);
      }
    });
  }

  private setupEndingDateSubscription(): void {
    this.endingDateSub = this.addClassForm.get('endingDate')?.valueChanges.subscribe(value => {
      if (value && this.selectedLearningCurve) {
        this.calculateStartingDateFromEnding(value);
      }
    });
  }

  private createLocalDateFromISOString(isoString: string): Date {
    // Use moment to parse ISO date string as local date to avoid timezone conversion
    // moment.parseZone preserves the date without timezone conversion
    if (!isoString) {
      return new Date();
    }

    // Parse as local date using moment
    // For ISO date strings like "2022-10-25", treat as local date
    const momentDate = moment(isoString.split('T')[0], 'YYYY-MM-DD', true);

    return momentDate.isValid() ? momentDate.toDate() : new Date(isoString);
  }

  formatPlanDate(isoDate: string): string {
    if (!isoDate) {
      return '';
    }

    // Use DateUtils.getShortDate3 which takes datePipe, date, and locale
    // This method uses the environment's dateFormat for the given locale
    return DateUtils.getShortDate3(this.datePipe, isoDate, this.locale);
  }

  get planStartDate(): string {
    return this.planSettings?.startDate ? this.formatPlanDate(this.planSettings.startDate) : '';
  }

  get planEndDate(): string {
    return this.planSettings?.endDate ? this.formatPlanDate(this.planSettings.endDate) : '';
  }
}
